import {
  getOfferControllerFindOneQueryKey,
  offerItemControllerExportAll,
  OfferStatus,
  useOfferControllerFindOne,
  useOfferControllerLock,
  useOfferControllerPing,
  useOfferControllerRelease,
  useOfferControllerTransition,
  useOfferMarkupControllerApplyCampaign,
  useOfferMarkupControllerCreate,
  useOfferMarkupControllerUpdate,
  type CreateOfferMarkupDto,
  type OfferControllerFindOneParams,
  type OfferLockDto,
  type OfferTransitionCompleteDto,
  type OfferTransitionDto,
  type OfferTransitionProcessTransactionDto,
  type OfferTransitionSelectItemsDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { computed, nextTick, onMounted, ref, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import { useLoading } from '../../stores/loadingStore';
import { useUser } from '../../stores/userStore';
import { useConsumer } from '../useConsumer';
import { useFileDownload } from '../useFileDownload';
import { useResponseData } from '../useResponseData';
import { useOfferImages } from './useOfferImages';

/** Offers in these states are on hold or done, so nothing changes behind the user's back. */
const UNPOLLED_OFFER_STATUS: OfferStatus[] = [
  OfferStatus.PAUSED,
  OfferStatus.DECLINED,
  OfferStatus.EXPIRED,
  OfferStatus.ABANDONED_INQUIRY,
  OfferStatus.CANCELLED,
  OfferStatus.HANDLING_COMPLETED,
];

/** An unknown status counts as pollable, so polling starts before the first response arrives. */
export const isPolledOfferStatus = (status?: OfferStatus) =>
  status === undefined || !UNPOLLED_OFFER_STATUS.includes(status);

export interface UseOfferOptions {
  refetchInterval?: MaybeRefOrGetter<number | false>;
  tryTakeLock?: MaybeRefOrGetter<boolean>;
}

export const useOffer = (
  offerId: Ref<number>,
  { refetchInterval = false, tryTakeLock = false }: UseOfferOptions = {},
) => {
  const { watchLoading, watchRefreshing } = useLoading();
  const { userId } = useUser();
  const { active: offerConsumerActive } = useConsumer('offer');

  const stateUpdated = ref(false);
  const findOfferParams = computed<OfferControllerFindOneParams>(() => ({
    tryTakeLock: toValue(tryTakeLock),
  }));
  const queryEnabled = computed(
    () => offerId.value >= 0 && stateUpdated.value && offerConsumerActive.value,
  );

  const {
    data,
    refetch: refetchOffer,
    isLoading: offerInitialLoading,
    isFetching: offerLoading,
  } = useOfferControllerFindOne(offerId, findOfferParams, {
    query: {
      queryKey: getOfferControllerFindOneQueryKey(offerId),
      enabled: queryEnabled,
      refetchInterval: (query) => {
        const interval = toValue(refetchInterval);

        if (!interval) return false;

        const response = query.state.data;
        const status = response?.status === 200 ? response.data.status : undefined;

        return isPolledOfferStatus(status) ? interval : false;
      },
    },
    request: {},
  });

  const refetchOfferIfActive = async () => {
    if (offerConsumerActive.value && !offerLoading.value) {
      await refetchOffer();
    }
  };

  const { mutateAsync: transitionOfferQuery, isPending: transitionOfferLoading } =
    useOfferControllerTransition({
      mutation: { onSuccess: () => refetchOfferIfActive() },
    });

  const { mutateAsync: acquireLockMutate, isPending: acquireLockLoading } = useOfferControllerLock({
    mutation: { onSuccess: () => refetchOfferIfActive() },
  });

  const { mutateAsync: releaseLockMutate, isPending: releaseLockLoading } =
    useOfferControllerRelease({
      mutation: { onSuccess: () => refetchOfferIfActive() },
    });

  const { mutateAsync: pingOfferMutate, isPending: pingOfferLoading } = useOfferControllerPing();

  const { mutateAsync: createOfferMarkupMutate, isPending: createOfferMarkupLoading } =
    useOfferMarkupControllerCreate({
      mutation: { onSuccess: () => refetchOfferIfActive() },
    });

  const { mutateAsync: updateOfferMarkupMutate, isPending: updateOfferMarkupLoading } =
    useOfferMarkupControllerUpdate({
      mutation: { onSuccess: () => refetchOfferIfActive() },
    });

  const { mutateAsync: applyMarkupCampaignMutate, isPending: applyMarkupCampaignLoading } =
    useOfferMarkupControllerApplyCampaign({
      mutation: { onSuccess: () => refetchOfferIfActive() },
    });

  const { downloadResponseAsFile, isDownloading } = useFileDownload({
    fileName: () => `export-offer-items-${offer.value?.id}-items.csv`,
    mime: 'text/csv',
  });

  const { createOfferImage, createOfferImages } = useOfferImages(offerId);

  const offer = useResponseData(data);

  /** Mirrors the query's own interval rule, so the UI can show that the offer is kept live. */
  const offerPolling = computed(
    () =>
      queryEnabled.value &&
      !!toValue(refetchInterval) &&
      isPolledOfferStatus(offer.value?.status),
  );

  const lockedBy = computed(() =>
    offer.value?.lockedBy && offer.value.lockedBy.id !== userId.value
      ? offer.value?.lockedBy
      : undefined,
  );
  const userHasLock = computed(() => {
    return offer.value?.lockedBy?.id === userId.value;
  });

  const transitionOffer = async (
    data:
      | OfferTransitionDto
      | OfferTransitionProcessTransactionDto
      | OfferTransitionSelectItemsDto
      | OfferTransitionCompleteDto,
  ) =>
    transitionOfferQuery(
      { offerId: offerId.value, data },
      { onSuccess: () => refetchOfferIfActive() },
    );

  const pingOffer = async () => pingOfferMutate({ offerId: offerId.value });

  const acquireLock = async (data: OfferLockDto) =>
    await acquireLockMutate({ offerId: offerId.value, data });

  const releaseLock = async (id?: number) =>
    await releaseLockMutate({ offerId: id ?? offerId.value });

  const createOfferMarkup = async (data: CreateOfferMarkupDto) =>
    await createOfferMarkupMutate({ offerId: offerId.value, data });

  const removeOfferMarkup = async (markupId: number) =>
    await updateOfferMarkupMutate({ offerId: offerId.value, markupId, data: { isActive: false } });

  /** Redeems a campaign code; the server derives the markup from the campaign. */
  const applyMarkupCampaign = async (code: string) =>
    await applyMarkupCampaignMutate({ offerId: offerId.value, data: { code } });

  const downloadOfferItemsCsv = () =>
    downloadResponseAsFile(offerItemControllerExportAll(offer.value?.id!));

  watchLoading(
    offerInitialLoading,
    transitionOfferLoading,
    releaseLockLoading,
    isDownloading,
    pingOfferLoading,
    createOfferMarkupLoading,
    updateOfferMarkupLoading,
    applyMarkupCampaignLoading,
  );

  // Polled refetches and the periodic lock renewal happen without the user asking for them.
  watchRefreshing(offerLoading, acquireLockLoading);

  onMounted(() => nextTick(() => (stateUpdated.value = true)));

  return {
    offer,
    lockedBy,
    userHasLock,
    offerLoading,
    offerPolling,
    transitionOffer,
    pingOffer,
    acquireLock,
    releaseLock,
    createOfferMarkup,
    removeOfferMarkup,
    applyMarkupCampaign,
    createOfferImage,
    createOfferImages,
    refetchOffer,
    downloadOfferItemsCsv,
  };
};
